import React, { useState, useEffect, useContext } from 'react';
import { Modal, Button, Form, ListGroup, Badge, Spinner, Alert } from 'react-bootstrap';
import { BsBuilding, BsPlusCircle, BsCheckCircle } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import AuthContext from '../context/AuthContext';
import { getMyBusinesses, createBusiness } from '../services/api';
import toast from 'react-hot-toast';

const BusinessSwitcherModal = ({ show, onHide }) => {
    const { user, business, switchBusiness } = useContext(AuthContext);
    const { t } = useTranslation();
    const [businesses, setBusinesses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newBusinessName, setNewBusinessName] = useState('');
    const [maxBusinesses, setMaxBusinesses] = useState(1);

    useEffect(() => {
        if (show) {
            fetchBusinesses();
        }
    }, [show]);

    const fetchBusinesses = async () => {
        setLoading(true);
        try {
            const response = await getMyBusinesses();
            setBusinesses(response.data);
            if (response.data.length > 0) {
                setMaxBusinesses(response.data[0].max_businesses || 1);
            }
        } catch (error) {
            console.error('Error fetching businesses:', error);
            toast.error('Error al cargar los negocios');
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchBusiness = async (targetBusinessId) => {
        if (targetBusinessId === business?.id) {
            onHide();
            return;
        }

        try {
            await switchBusiness(targetBusinessId);
            onHide();
        } catch (error) {
            console.error('Error switching business:', error);
        }
    };

    const handleCreateBusiness = async (e) => {
        e.preventDefault();
        if (!newBusinessName.trim()) {
            toast.error('El nombre del negocio es requerido');
            return;
        }

        setCreating(true);
        try {
            await createBusiness({ name: newBusinessName });
            toast.success('Negocio creado exitosamente');
            setNewBusinessName('');
            setShowCreateForm(false);
            await fetchBusinesses();
        } catch (error) {
            console.error('Error creating business:', error);
            const errorMsg = error.response?.data?.message || 'Error al crear el negocio';
            toast.error(errorMsg);
        } finally {
            setCreating(false);
        }
    };

    const canCreateMore = maxBusinesses === -1 || businesses.length < maxBusinesses;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <BsBuilding className="me-2" />
                    Mis Negocios
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <>
                        <ListGroup className="mb-3">
                            {businesses.map((biz) => (
                                <ListGroup.Item
                                    key={biz.id}
                                    action
                                    onClick={() => handleSwitchBusiness(biz.id)}
                                    className="d-flex justify-content-between align-items-center"
                                    active={biz.id === business?.id}
                                >
                                    <span>
                                        {biz.name}
                                        {biz.id === business?.id && (
                                            <BsCheckCircle className="ms-2 text-success" />
                                        )}
                                    </span>
                                    <div className="d-flex gap-1">
                                        <Badge bg={biz.is_owner ? 'primary' : 'info'} pill>
                                            {t(`usersPage.roles.${biz.user_role}`, biz.user_role)}
                                        </Badge>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>

                        {!showCreateForm && canCreateMore && (
                            <Button
                                variant="outline-primary"
                                className="w-100"
                                onClick={() => setShowCreateForm(true)}
                            >
                                <BsPlusCircle className="me-2" />
                                Crear Nuevo Negocio
                            </Button>
                        )}

                        {!canCreateMore && (
                            <Alert variant="warning" className="mb-0">
                                Has alcanzado el límite de negocios de tu plan ({maxBusinesses}).
                                Actualiza tu plan para crear más negocios.
                            </Alert>
                        )}

                        {showCreateForm && (
                            <div className="mt-3 p-3 border rounded">
                                <h6>Crear Nuevo Negocio</h6>
                                <Form onSubmit={handleCreateBusiness}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Nombre del Negocio</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={newBusinessName}
                                            onChange={(e) => setNewBusinessName(e.target.value)}
                                            placeholder="Ej: Sucursal Norte"
                                            disabled={creating}
                                            required
                                        />
                                    </Form.Group>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="primary"
                                            type="submit"
                                            disabled={creating}
                                            className="flex-grow-1"
                                        >
                                            {creating ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        className="me-2"
                                                    />
                                                    Creando...
                                                </>
                                            ) : (
                                                'Crear'
                                            )}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={() => {
                                                setShowCreateForm(false);
                                                setNewBusinessName('');
                                            }}
                                            disabled={creating}
                                        >
                                            Cancelar
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default BusinessSwitcherModal;
